import React, { useState, useEffect, useRef } from 'react';
import { CircularProgress } from '@mui/material';
import {
  ArrowBack as BackIcon,
  ConfirmationNumber as TicketIcon,
  Person as PersonIcon,
  Schedule as TimelineIcon,
  Settings as ActionsIcon,
  Message as MessageIcon,
  Send as SendIcon,
  AttachFile as AttachIcon,
  Image as ImageIcon,
  Close as CloseIcon,
  ZoomIn as ZoomIcon,
  FlashOn as QuickIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Assignment as AssignIcon,
  Category as CategoryIcon,
  Flag as PriorityIcon,
  Note as NoteIcon,
  FolderOpen as OpenIcon,
  Autorenew as InProgressIcon,
  DoneAll as ResolvedIcon,
  Block as ClosedIcon,
  HourglassEmpty as WaitingIcon,
  History as HistoryIcon,
  SwapHoriz as ChangeIcon,
  TrendingUp as EscalateIcon,
  LowPriority as LowPriorityIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import adminTicketService from '../../services/adminTicketService';
import cannedResponsesService from '../../services/cannedResponses.service';
import ticketMacrosService from '../../services/ticketMacros.service';
import imageUploadService from '../../services/imageUpload.service';
import { Ticket, TicketStatus, TicketPriority, TicketCategory } from '../../types/ticket';
import MyShiftStatusBanner from '../../components/common/MyShiftStatusBanner';
import './TicketStyles.css';
import { API_BASE_URL } from '../../config/env';

interface TicketMessage {
  _id: string;
  message: string;
  senderType: 'USER' | 'ADMIN';
  isInternal: boolean;
  senderId: {
    _id: string;
    name: string;
    email: string;
  };
  attachments: Array<{
    fileName: string;
    originalName: string;
    size: number;
    mimeType: string;
    data?: string;
    url?: string;
  }>;
  createdAt: string;
}

const getAttachmentSrc = (attachment: { data?: string; url?: string; fileName?: string }): string => {
  if (attachment.url) return attachment.url;
  if (attachment.data) return attachment.data;
  return `${API_BASE_URL}/uploads/${attachment.fileName}`;
};

const AdminTicketDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [messages, setMessages] = useState<TicketMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [imageAttachments, setImageAttachments] = useState<File[]>([]);

  // Modal states
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedImageName, setSelectedImageName] = useState<string>('');

  // Dialog states
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [priorityDialogOpen, setPriorityDialogOpen] = useState(false);
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [newPriority, setNewPriority] = useState('');
  const [changeNotes, setChangeNotes] = useState('');
  const [adminUsers, setAdminUsers] = useState<Array<{ _id: string; name: string; email: string }>>([]);
  const [selectedAdmin, setSelectedAdmin] = useState('');
  const [reassignNotes, setReassignNotes] = useState('');
  const [updating, setUpdating] = useState(false);

  // Quick actions
  const [quickMenuOpen, setQuickMenuOpen] = useState(false);
  const [canned, setCanned] = useState<Array<{ _id: string; title: string; content: string }>>([]);
  const [macros, setMacros] = useState<Array<{ _id: string; name: string }>>([]);
  const quickMenuRef = useRef<HTMLDivElement>(null);

  // Ticket history
  const [ticketHistory, setTicketHistory] = useState<
    Array<{
      _id: string;
      action: string;
      actionLabel: string;
      changes: Array<{
        field: string;
        oldValue: any;
        newValue: any;
        fieldLabel: string;
      }>;
      userId: { _id: string; name: string; email: string } | null;
      timestamp: string;
    }>
  >([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    if (id) {
      loadTicket(id);
      loadMessages(id);
      loadHistory(id);
    }
  }, [id]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (imageModalOpen) setImageModalOpen(false);
        if (quickMenuOpen) setQuickMenuOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [imageModalOpen, quickMenuOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (quickMenuRef.current && !quickMenuRef.current.contains(event.target as Node)) {
        setQuickMenuOpen(false);
      }
    };
    if (quickMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [quickMenuOpen]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadTicket = async (ticketId: string) => {
    try {
      setLoading(true);
      setError(null);
      const ticketData = await adminTicketService.getTicketById(ticketId);
      setTicket(ticketData);
    } catch (err) {
      console.error('Error loading ticket:', err);
      setError('خطا در بارگذاری جزئیات تیکت');
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (ticketId: string) => {
    try {
      setLoadingMessages(true);
      const messagesData = await adminTicketService.getTicketMessages(ticketId);
      setMessages(messagesData);
      setTimeout(scrollToBottom, 100);
    } catch (err) {
      console.error('Error loading messages:', err);
    } finally {
      setLoadingMessages(false);
    }
  };

  const loadHistory = async (ticketId: string) => {
    try {
      setLoadingHistory(true);
      const result = await adminTicketService.getTicketHistory(ticketId);
      setTicketHistory(result.history);
    } catch (err) {
      console.error('Error loading history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const openQuickMenu = async () => {
    setQuickMenuOpen(true);
    try {
      if (canned.length === 0) {
        const list = await cannedResponsesService.list('');
        setCanned(list.map((x: any) => ({ _id: x._id, title: x.title, content: x.content })));
      }
      if (macros.length === 0) {
        const list = await ticketMacrosService.list();
        setMacros(list.map((x: any) => ({ _id: x._id, name: x.name })));
      }
    } catch {
      // Silent fail
    }
  };

  const applyCanned = (content: string) => {
    setNewMessage((prev) => (prev ? prev + '\n\n' + content : content));
    setQuickMenuOpen(false);
  };

  const executeMacro = async (macroId: string) => {
    if (!id) return;
    try {
      await ticketMacrosService.execute(macroId, id);
      setSuccessMessage('ماکرو با موفقیت اجرا شد');
      await loadTicket(id);
      await loadMessages(id);
    } catch {
      setError('اجرای ماکرو با خطا مواجه شد');
    } finally {
      setQuickMenuOpen(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !id) return;

    try {
      setSendingMessage(true);

      const attachmentUrls: string[] = [];
      for (const file of imageAttachments) {
        try {
          const uploadResult = await imageUploadService.uploadImage(file, 'tickets');
          if (uploadResult?.url) {
            attachmentUrls.push(uploadResult.url);
          }
        } catch (uploadError) {
          console.error('Error uploading attachment:', uploadError);
        }
      }

      await adminTicketService.sendMessage(id, newMessage.trim(), isInternal, attachmentUrls);
      setNewMessage('');
      setIsInternal(false);
      setImageAttachments([]);
      await loadMessages(id);
    } catch (err) {
      console.error('Error sending message:', err);
      setError('خطا در ارسال پیام');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const imageFiles = Array.from(files).filter((file) => file.type.startsWith('image/'));
      setImageAttachments([...imageAttachments, ...imageFiles]);
    }
  };

  const removeImage = (index: number) => {
    setImageAttachments(imageAttachments.filter((_, i) => i !== index));
  };

  const handleImageClick = (imageSrc: string, imageName: string) => {
    setSelectedImage(imageSrc);
    setSelectedImageName(imageName);
    setImageModalOpen(true);
  };

  const handleStatusChange = async () => {
    if (!newStatus || !id) return;
    try {
      setUpdating(true);
      await adminTicketService.changeTicketStatus(id, newStatus, changeNotes);
      setStatusDialogOpen(false);
      setNewStatus('');
      setChangeNotes('');
      setSuccessMessage('وضعیت تیکت با موفقیت تغییر یافت');
      await loadTicket(id);
    } catch {
      setError('خطا در تغییر وضعیت تیکت');
    } finally {
      setUpdating(false);
    }
  };

  const handleCategoryChange = async () => {
    if (!newCategory || !id) return;
    try {
      setUpdating(true);
      await adminTicketService.updateTicketCategory(id, newCategory, changeNotes);
      setCategoryDialogOpen(false);
      setNewCategory('');
      setChangeNotes('');
      setSuccessMessage('دسته‌بندی تیکت با موفقیت تغییر یافت');
      await loadTicket(id);
    } catch {
      setError('خطا در تغییر دسته‌بندی تیکت');
    } finally {
      setUpdating(false);
    }
  };

  const handlePriorityChange = async () => {
    if (!newPriority || !id) return;
    try {
      setUpdating(true);
      await adminTicketService.updateTicketPriority(id, newPriority, changeNotes);
      setPriorityDialogOpen(false);
      setNewPriority('');
      setChangeNotes('');
      setSuccessMessage('اولویت تیکت با موفقیت تغییر یافت');
      await loadTicket(id);
    } catch {
      setError('خطا در تغییر اولویت تیکت');
    } finally {
      setUpdating(false);
    }
  };

  const handleReassign = async () => {
    if (!selectedAdmin || !id) return;
    try {
      setUpdating(true);
      await adminTicketService.assignTicket(id, selectedAdmin, reassignNotes || undefined);
      setReassignDialogOpen(false);
      setSelectedAdmin('');
      setReassignNotes('');
      setSuccessMessage('تخصیص با موفقیت انجام شد');
      await loadTicket(id);
    } catch {
      setError('خطا در تخصیص مجدد');
    } finally {
      setUpdating(false);
    }
  };

  const openReassignDialog = async () => {
    try {
      const list = await adminTicketService.getAdminUsers();
      setAdminUsers(Array.isArray(list) ? list : []);
    } catch {
      setAdminUsers([]);
    }
    setSelectedAdmin(ticket?.assignedTo?._id || '');
    setReassignNotes('');
    setReassignDialogOpen(true);
  };

  const getStatusText = (status: TicketStatus | string) => {
    const statusStr = String(status).toLowerCase();
    const map: Record<string, string> = {
      open: 'باز',
      in_progress: 'در حال بررسی',
      resolved: 'حل شده',
      closed: 'بسته',
      waiting_for_response: 'در انتظار پاسخ',
    };
    return map[statusStr] || status;
  };

  const getPriorityText = (priority: TicketPriority | string) => {
    const priorityStr = String(priority).toLowerCase();
    const map: Record<string, string> = {
      low: 'کم',
      medium: 'متوسط',
      high: 'بالا',
      urgent: 'فوری',
    };
    return map[priorityStr] || priority;
  };

  const getCategoryText = (category: TicketCategory | string) => {
    const categoryStr = String(category).toLowerCase();
    const map: Record<string, string> = {
      technical_support: 'پشتیبانی فنی',
      technical: 'پشتیبانی فنی',
      financial: 'مالی',
      general_inquiry: 'سوال عمومی',
      general: 'سوال عمومی',
      bug_report: 'گزارش باگ',
      bug: 'گزارش باگ',
      feature_request: 'درخواست قابلیت',
      feature: 'درخواست قابلیت',
      account: 'حساب کاربری',
      payment: 'پرداخت',
      order: 'سفارش',
      product: 'محصول',
      complaint: 'شکایت',
      suggestion: 'پیشنهاد',
    };
    return map[categoryStr] || category;
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('fa-IR');
  };

  const formatTime = (date: string) => {
    return new Date(date).toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDateTime = (date: string) => {
    return `${formatDate(date)} - ${formatTime(date)}`;
  };

  if (loading) {
    return (
      <div className="ticket-page">
        <div className="loading-overlay">
          <CircularProgress size={40} />
          <p>در حال بارگذاری...</p>
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="ticket-page">
        <div className="empty-state">
          <TicketIcon />
          <h3>تیکت یافت نشد</h3>
          <p>تیکت مورد نظر وجود ندارد یا حذف شده است</p>
          <button className="header-btn primary" style={{ marginTop: 16 }} onClick={() => navigate('/tickets/list')}>
            <BackIcon style={{ transform: 'rotate(180deg)' }} />
            بازگشت به لیست تیکت‌ها
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>تیکت #{ticket.ticketNumber || ticket._id.slice(-6)}</title>
      </Helmet>

      <div className="ticket-page">
        <MyShiftStatusBanner category="ticket" />
        {/* Header */}
        <div className="ticket-detail-header">
          <div className="ticket-detail-header-top">
            <div className="ticket-detail-title-section">
              <button className="ticket-detail-back" onClick={() => navigate('/tickets/list')}>
                <BackIcon />
                بازگشت به لیست
              </button>
              <div className="ticket-detail-title">
                <h1>
                  <TicketIcon />
                  جزئیات تیکت
                  <span className="ticket-number">#{ticket.ticketNumber || ticket._id.slice(-6)}</span>
                </h1>
              </div>
            </div>

            <div className="ticket-detail-badges">
              <span className={`detail-badge status-${String(ticket.status).toLowerCase()}`}>
                {(ticket.status === TicketStatus.OPEN || String(ticket.status).toLowerCase() === 'open') && (
                  <OpenIcon style={{ fontSize: 14 }} />
                )}
                {(ticket.status === TicketStatus.IN_PROGRESS ||
                  String(ticket.status).toLowerCase() === 'in_progress') && <InProgressIcon style={{ fontSize: 14 }} />}
                {(ticket.status === TicketStatus.RESOLVED || String(ticket.status).toLowerCase() === 'resolved') && (
                  <ResolvedIcon style={{ fontSize: 14 }} />
                )}
                {(ticket.status === TicketStatus.CLOSED || String(ticket.status).toLowerCase() === 'closed') && (
                  <ClosedIcon style={{ fontSize: 14 }} />
                )}
                {String(ticket.status).toLowerCase() === 'waiting_for_response' && (
                  <WaitingIcon style={{ fontSize: 14 }} />
                )}
                {getStatusText(ticket.status)}
              </span>
              <span className={`detail-badge priority-${ticket.priority.toLowerCase()}`}>
                <PriorityIcon style={{ fontSize: 14 }} />
                {getPriorityText(ticket.priority)}
              </span>
              {ticket.responseDueAt && (
                <span
                  className={`detail-badge ${
                    new Date(ticket.responseDueAt) < new Date() ? 'sla-danger' : 'sla-warning'
                  }`}
                >
                  مهلت پاسخ: {formatDateTime(ticket.responseDueAt)}
                </span>
              )}
              {ticket.escalated && (
                <span className="detail-badge escalated">
                  تشدید شده {typeof ticket.escalationLevel === 'number' && `(سطح ${ticket.escalationLevel})`}
                </span>
              )}
            </div>
          </div>

          <div className="ticket-detail-header-actions">
            <div style={{ position: 'relative' }} ref={quickMenuRef}>
              <button className="header-btn primary" onClick={openQuickMenu}>
                <QuickIcon style={{ fontSize: 18 }} />
                اکشن سریع
              </button>
              {quickMenuOpen && (
                <div className="quick-actions-menu">
                  <div className="quick-menu-section">
                    <div className="quick-menu-title">پاسخ‌های آماده</div>
                    {canned.length === 0 ? (
                      <div className="quick-menu-empty">موردی یافت نشد</div>
                    ) : (
                      canned.slice(0, 8).map((c) => (
                        <div key={c._id} className="quick-menu-item" onClick={() => applyCanned(c.content)}>
                          {c.title}
                        </div>
                      ))
                    )}
                  </div>
                  <div className="quick-menu-section">
                    <div className="quick-menu-title">ماکروها</div>
                    {macros.length === 0 ? (
                      <div className="quick-menu-empty">موردی یافت نشد</div>
                    ) : (
                      macros.slice(0, 8).map((m) => (
                        <div key={m._id} className="quick-menu-item" onClick={() => executeMacro(m._id)}>
                          {m.name}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
            <button className="header-btn" onClick={() => id && loadTicket(id)}>
              <RefreshIcon style={{ fontSize: 18 }} />
              بروزرسانی
            </button>
          </div>
        </div>

        {/* Alerts */}
        {successMessage && (
          <div className="alert-message success">
            <SuccessIcon />
            {successMessage}
            <button className="alert-close" onClick={() => setSuccessMessage(null)}>
              <CloseIcon style={{ fontSize: 18 }} />
            </button>
          </div>
        )}

        {error && (
          <div className="alert-message error">
            <ErrorIcon />
            {error}
            <button className="alert-close" onClick={() => setError(null)}>
              <CloseIcon style={{ fontSize: 18 }} />
            </button>
          </div>
        )}

        {/* Main Layout */}
        <div className="ticket-detail-layout">
          {/* Main Content */}
          <div className="ticket-main-content">
            {/* Ticket Info Card */}
            <div className="detail-card">
              <div className="detail-card-header">
                <h3 className="detail-card-title">
                  <TicketIcon />
                  اطلاعات تیکت
                </h3>
              </div>
              <div className="detail-card-body">
                <h2 className="ticket-subject">{ticket.subject}</h2>
                <p className="ticket-description">{ticket.description}</p>

                <div className="ticket-info-grid">
                  <div className="info-item">
                    <span className="info-item-label">دسته‌بندی</span>
                    <span className="info-item-value">{getCategoryText(ticket.category)}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-item-label">تاریخ ایجاد</span>
                    <span className="info-item-value">{formatDateTime(ticket.createdAt)}</span>
                  </div>
                  {ticket.assignedTo && (
                    <>
                      <div className="info-item">
                        <span className="info-item-label">تخصیص یافته به</span>
                        <span className="info-item-value">{ticket.assignedTo.name}</span>
                      </div>
                      <div className="info-item">
                        <span className="info-item-label">تاریخ تخصیص</span>
                        <span className="info-item-value">
                          {ticket.assignedAt ? formatDateTime(ticket.assignedAt) : '-'}
                        </span>
                      </div>
                    </>
                  )}
                </div>

                {/* Attachments */}
                {ticket.attachments && ticket.attachments.length > 0 && (
                  <div className="attachments-section">
                    <div className="attachments-title">
                      <AttachIcon />
                      ضمیمه‌های تیکت ({ticket.attachments.length})
                    </div>
                    <div className="attachments-grid">
                      {ticket.attachments.map((attachment, index) => (
                        <div
                          key={index}
                          className="attachment-item"
                          onClick={() =>
                            attachment.mimeType?.startsWith('image/') &&
                            handleImageClick(getAttachmentSrc(attachment), attachment.originalName)
                          }
                        >
                          {attachment.mimeType?.startsWith('image/') ? (
                            <div className="attachment-preview">
                              <img src={getAttachmentSrc(attachment)} alt={attachment.originalName} />
                              <div className="attachment-preview-overlay">
                                <ZoomIcon />
                              </div>
                            </div>
                          ) : (
                            <div className="attachment-icon-only">
                              <AttachIcon />
                            </div>
                          )}
                          <div className="attachment-info">
                            <div className="attachment-name">{attachment.originalName}</div>
                            <div className="attachment-size">{(attachment.size / 1024).toFixed(1)} KB</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Messages Card */}
            <div className="detail-card">
              <div className="detail-card-header">
                <h3 className="detail-card-title">
                  <MessageIcon />
                  پیام‌ها و پاسخ‌ها
                </h3>
                <span className="table-count">{messages.length} پیام</span>
              </div>

              {loadingMessages ? (
                <div className="loading-overlay" style={{ padding: 40 }}>
                  <CircularProgress size={30} />
                  <p>در حال بارگذاری پیام‌ها...</p>
                </div>
              ) : (
                <div className="messages-container">
                  {messages.length === 0 ? (
                    <div className="messages-empty">
                      <MessageIcon />
                      <p>هنوز پیامی ارسال نشده است</p>
                    </div>
                  ) : (
                    messages.map((message) => (
                      <div
                        key={message._id}
                        className={`message-item ${message.senderType.toLowerCase()} ${
                          message.isInternal ? 'internal' : ''
                        }`}
                      >
                        <div className="message-bubble">
                          <div className="message-header">
                            <div className="message-avatar">{message.senderId.name.charAt(0)}</div>
                            <span className="message-sender">{message.senderId.name}</span>
                            {message.senderType === 'ADMIN' && <span className="message-badge admin">ادمین</span>}
                            {message.isInternal && <span className="message-badge internal">داخلی</span>}
                          </div>
                          <div className="message-content">{message.message}</div>

                          {message.attachments && message.attachments.length > 0 && (
                            <div className="message-attachments">
                              {message.attachments.map(
                                (attachment, idx) =>
                                  attachment.mimeType?.startsWith('image/') && (
                                    <div
                                      key={idx}
                                      className="message-attachment"
                                      onClick={() =>
                                        handleImageClick(getAttachmentSrc(attachment), attachment.originalName)
                                      }
                                    >
                                      <img src={getAttachmentSrc(attachment)} alt={attachment.originalName} />
                                    </div>
                                  )
                              )}
                            </div>
                          )}
                        </div>
                        <div className="message-time">{formatDateTime(message.createdAt)}</div>
                      </div>
                    ))
                  )}
                  <div ref={messagesEndRef} />
                </div>
              )}

              {/* Message Composer */}
              <div className="message-composer">
                <div className="composer-title">ارسال پاسخ</div>
                <textarea
                  className="composer-textarea"
                  placeholder="پاسخ خود را اینجا بنویسید..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                />

                {imageAttachments.length > 0 && (
                  <div className="composer-attachments">
                    {imageAttachments.map((file, index) => (
                      <div key={index} className="composer-attachment-item">
                        <img src={URL.createObjectURL(file)} alt={file.name} />
                        <button className="composer-attachment-remove" onClick={() => removeImage(index)}>
                          <CloseIcon />
                        </button>
                        <div className="composer-attachment-name">
                          {file.name.length > 10 ? file.name.substring(0, 10) + '...' : file.name}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="composer-actions">
                  <div className="composer-options">
                    <label className="composer-checkbox">
                      <input type="checkbox" checked={isInternal} onChange={(e) => setIsInternal(e.target.checked)} />
                      پیام داخلی (فقط برای ادمین‌ها)
                    </label>
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="message-image-upload"
                      onChange={handleImageUpload}
                    />
                    <label htmlFor="message-image-upload" className="composer-upload-btn">
                      <ImageIcon style={{ fontSize: 18 }} />
                      افزودن عکس
                    </label>
                  </div>
                  <button
                    className="composer-send-btn"
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim() || sendingMessage}
                  >
                    {sendingMessage ? (
                      <>
                        <CircularProgress size={18} style={{ color: '#fff' }} />
                        در حال ارسال...
                      </>
                    ) : (
                      <>
                        <SendIcon style={{ fontSize: 18 }} />
                        ارسال پاسخ
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="ticket-sidebar">
            {/* User Info */}
            <div className="sidebar-card">
              <div className="sidebar-card-header">
                <PersonIcon />
                اطلاعات کاربر
              </div>
              <div className="sidebar-card-body">
                <div className="user-info">
                  <div className="user-avatar">{ticket.userId?.name?.charAt(0) || 'N'}</div>
                  <div className="user-details">
                    <div className="user-name">{ticket.userId?.name || 'کاربر نامشخص'}</div>
                    <div className="user-email">{ticket.userId?.email || 'ایمیل در دسترس نیست'}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Timeline */}
            <div className="sidebar-card">
              <div className="sidebar-card-header">
                <TimelineIcon />
                روند زمانی
              </div>
              <div className="sidebar-card-body">
                <div className="timeline-list">
                  <div className="timeline-item">
                    <div className="timeline-dot"></div>
                    <div className="timeline-content">
                      <div className="timeline-label">ایجاد تیکت</div>
                      <div className="timeline-value">{formatDateTime(ticket.createdAt)}</div>
                    </div>
                  </div>
                  {ticket.assignedAt && (
                    <div className="timeline-item">
                      <div className="timeline-dot"></div>
                      <div className="timeline-content">
                        <div className="timeline-label">تخصیص تیکت</div>
                        <div className="timeline-value">{formatDateTime(ticket.assignedAt)}</div>
                      </div>
                    </div>
                  )}
                  {ticket.lastResponseAt && (
                    <div className="timeline-item">
                      <div className="timeline-dot"></div>
                      <div className="timeline-content">
                        <div className="timeline-label">آخرین پاسخ</div>
                        <div className="timeline-value">{formatDateTime(ticket.lastResponseAt)}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Actions - Redesigned */}
            <div className="sidebar-card actions-card">
              <div className="sidebar-card-header">
                <ActionsIcon />
                عملیات سریع
              </div>
              <div className="sidebar-card-body">
                <div className="actions-grid">
                  <button className="action-tile" onClick={openReassignDialog} disabled={!ticket.canBeAssigned}>
                    <div className="action-tile-icon assign">
                      <AssignIcon />
                    </div>
                    <span className="action-tile-label">تخصیص</span>
                  </button>
                  <button
                    className="action-tile"
                    onClick={() => setStatusDialogOpen(true)}
                    disabled={ticket.status === TicketStatus.CLOSED}
                  >
                    <div className="action-tile-icon status">
                      <ChangeIcon />
                    </div>
                    <span className="action-tile-label">وضعیت</span>
                  </button>
                  <button className="action-tile" onClick={() => setCategoryDialogOpen(true)}>
                    <div className="action-tile-icon category">
                      <CategoryIcon />
                    </div>
                    <span className="action-tile-label">دسته‌بندی</span>
                  </button>
                  <button className="action-tile" onClick={() => setPriorityDialogOpen(true)}>
                    <div className="action-tile-icon priority">
                      <PriorityIcon />
                    </div>
                    <span className="action-tile-label">اولویت</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Ticket History */}
            <div className="sidebar-card history-card">
              <div className="sidebar-card-header">
                <HistoryIcon />
                تاریخچه تغییرات
              </div>
              <div className="sidebar-card-body">
                {loadingHistory ? (
                  <div className="history-loading">
                    <CircularProgress size={24} />
                    <span>در حال بارگذاری...</span>
                  </div>
                ) : ticketHistory.length === 0 ? (
                  <div className="history-empty">
                    <HistoryIcon />
                    <span>تاریخچه‌ای موجود نیست</span>
                  </div>
                ) : (
                  <div className="history-list">
                    {ticketHistory.slice(0, 10).map((entry) => (
                      <div key={entry._id} className="history-item">
                        <div className="history-item-header">
                          <span className={`history-action ${entry.action}`}>{entry.actionLabel}</span>
                          <span className="history-time">{formatDateTime(entry.timestamp)}</span>
                        </div>
                        {entry.userId && <div className="history-user">توسط: {entry.userId.name}</div>}
                        {entry.changes.length > 0 && (
                          <div className="history-changes">
                            {entry.changes.map((change, idx) => (
                              <div key={idx} className="history-change">
                                <span className="change-field">{change.fieldLabel}:</span>
                                <span className="change-values">
                                  {change.oldValue && <span className="old-value">{String(change.oldValue)}</span>}
                                  {change.oldValue && change.newValue && ' → '}
                                  {change.newValue && <span className="new-value">{String(change.newValue)}</span>}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {imageModalOpen && selectedImage && (
        <div className="image-modal-overlay" onClick={() => setImageModalOpen(false)}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="image-modal-close" onClick={() => setImageModalOpen(false)}>
              <CloseIcon />
            </button>
            <img src={selectedImage} alt={selectedImageName} className="image-modal-img" />
            <div className="image-modal-name">{selectedImageName}</div>
          </div>
        </div>
      )}

      {/* Status Dialog */}
      {statusDialogOpen && (
        <div className="dialog-overlay">
          <div className="dialog">
            <div className="dialog-header">
              <h2>
                <EditIcon />
                تغییر وضعیت تیکت
              </h2>
              <button className="dialog-close" onClick={() => setStatusDialogOpen(false)}>
                <CloseIcon />
              </button>
            </div>
            <div className="dialog-content">
              <div className="form-group">
                <label className="form-label">وضعیت جدید</label>
                <select className="form-select" value={newStatus} onChange={(e) => setNewStatus(e.target.value)}>
                  <option value="">انتخاب کنید...</option>
                  <option value="open">باز</option>
                  <option value="in_progress">در حال بررسی</option>
                  <option value="waiting_for_response">در انتظار پاسخ</option>
                  <option value="resolved">حل شده</option>
                  <option value="closed">بسته شده</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">یادداشت (اختیاری)</label>
                <textarea
                  className="form-textarea"
                  placeholder="دلیل تغییر وضعیت..."
                  value={changeNotes}
                  onChange={(e) => setChangeNotes(e.target.value)}
                />
              </div>
            </div>
            <div className="dialog-actions">
              <button className="header-btn" onClick={() => setStatusDialogOpen(false)}>
                لغو
              </button>
              <button className="header-btn primary" onClick={handleStatusChange} disabled={!newStatus || updating}>
                {updating ? 'در حال تغییر...' : 'تغییر وضعیت'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Category Dialog */}
      {categoryDialogOpen && (
        <div className="dialog-overlay">
          <div className="dialog">
            <div className="dialog-header">
              <h2>
                <CategoryIcon />
                تغییر دسته‌بندی تیکت
              </h2>
              <button className="dialog-close" onClick={() => setCategoryDialogOpen(false)}>
                <CloseIcon />
              </button>
            </div>
            <div className="dialog-content">
              <div className="form-group">
                <label className="form-label">دسته‌بندی جدید</label>
                <select className="form-select" value={newCategory} onChange={(e) => setNewCategory(e.target.value)}>
                  <option value="">انتخاب کنید...</option>
                  <option value="technical_support">پشتیبانی فنی</option>
                  <option value="financial">مالی</option>
                  <option value="general_inquiry">سوال عمومی</option>
                  <option value="bug_report">گزارش باگ</option>
                  <option value="feature_request">درخواست قابلیت</option>
                  <option value="account">حساب کاربری</option>
                  <option value="payment">پرداخت</option>
                  <option value="order">سفارش</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">یادداشت (اختیاری)</label>
                <textarea
                  className="form-textarea"
                  placeholder="دلیل تغییر دسته‌بندی..."
                  value={changeNotes}
                  onChange={(e) => setChangeNotes(e.target.value)}
                />
              </div>
            </div>
            <div className="dialog-actions">
              <button className="header-btn" onClick={() => setCategoryDialogOpen(false)}>
                لغو
              </button>
              <button className="header-btn primary" onClick={handleCategoryChange} disabled={!newCategory || updating}>
                {updating ? 'در حال تغییر...' : 'تغییر دسته‌بندی'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Priority Dialog */}
      {priorityDialogOpen && (
        <div className="dialog-overlay">
          <div className="dialog">
            <div className="dialog-header">
              <h2>
                <PriorityIcon />
                تغییر اولویت تیکت
              </h2>
              <button className="dialog-close" onClick={() => setPriorityDialogOpen(false)}>
                <CloseIcon />
              </button>
            </div>
            <div className="dialog-content">
              <div className="form-group">
                <label className="form-label">اولویت جدید</label>
                <select className="form-select" value={newPriority} onChange={(e) => setNewPriority(e.target.value)}>
                  <option value="">انتخاب کنید...</option>
                  <option value="low">کم</option>
                  <option value="medium">متوسط</option>
                  <option value="high">بالا</option>
                  <option value="urgent">فوری</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">یادداشت (اختیاری)</label>
                <textarea
                  className="form-textarea"
                  placeholder="دلیل تغییر اولویت..."
                  value={changeNotes}
                  onChange={(e) => setChangeNotes(e.target.value)}
                />
              </div>
            </div>
            <div className="dialog-actions">
              <button className="header-btn" onClick={() => setPriorityDialogOpen(false)}>
                لغو
              </button>
              <button className="header-btn primary" onClick={handlePriorityChange} disabled={!newPriority || updating}>
                {updating ? 'در حال تغییر...' : 'تغییر اولویت'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reassign Dialog */}
      {reassignDialogOpen && (
        <div className="dialog-overlay">
          <div className="dialog">
            <div className="dialog-header">
              <h2>
                <AssignIcon />
                تخصیص مجدد تیکت
              </h2>
              <button className="dialog-close" onClick={() => setReassignDialogOpen(false)}>
                <CloseIcon />
              </button>
            </div>
            <div className="dialog-content">
              <div className="form-group">
                <label className="form-label">انتخاب ادمین</label>
                <select
                  className="form-select"
                  value={selectedAdmin}
                  onChange={(e) => setSelectedAdmin(e.target.value)}
                >
                  <option value="">انتخاب کنید...</option>
                  {adminUsers.map((u) => (
                    <option key={u._id} value={u._id}>
                      {u.name} ({u.email})
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">یادداشت (اختیاری)</label>
                <textarea
                  className="form-textarea"
                  placeholder="یادداشت برای تخصیص..."
                  value={reassignNotes}
                  onChange={(e) => setReassignNotes(e.target.value)}
                />
              </div>
            </div>
            <div className="dialog-actions">
              <button className="header-btn" onClick={() => setReassignDialogOpen(false)}>
                انصراف
              </button>
              <button className="header-btn primary" onClick={handleReassign} disabled={!selectedAdmin || updating}>
                {updating ? 'در حال تخصیص...' : 'تأیید تخصیص'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminTicketDetail;
